import React, { useState, useCallback, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { getWebAbsPath } from '../util/path';

const TEAL = '#00A8BB';
const PURPLE = '#593687';
const FONT = 'Montserrat, sans-serif';

const CONTENT_GAP = 16;

const useStyles = makeStyles((theme) => ({
    paper: {
        width: '90%',
        maxWidth: 900,
        maxHeight: 'min(80vh, 720px)',
        margin: 16,
        borderRadius: 8,
        padding: '0 4px 10px',
        [theme.breakpoints.down('sm')]: {
            width: '95%',
            maxWidth: 480,
            maxHeight: 'min(85vh, 640px)',
            padding: 0,
        },
    },
    content: {
        padding: '20px 32px',
        overflowY: 'auto',
        [theme.breakpoints.down('sm')]: {
            padding: 20,
            paddingBottom: theme.spacing(1),
            overflowY: 'unset',
        },
    },
    innerContent: {
        padding: '0 10px',
        overflowY: 'unset',
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            overflowY: 'auto',
            maxHeight: 'min(40vh, 350px)',
        },
    },
    title: {
        color: TEAL,
        fontFamily: FONT,
        fontSize: 39,
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: 'normal',
        letterSpacing: 0.15,
        margin: 0,
        marginBottom: 38,
        whiteSpace: 'pre-line',
        [theme.breakpoints.down('sm')]: {
            fontSize: 24,
            marginBottom: 10,
        },
    },
    mobileMedia: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
            width: '100%',
            borderRadius: 8,
            marginBottom: 10,
        },
    },
    row: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: theme.spacing(4),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            gap: CONTENT_GAP,
        },
    },
    leftCol: {
        flex: 1,
        minWidth: 0,
    },
    rightCol: {
        flexShrink: 0,
        width: '46%',
        alignSelf: 'center',
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        },
    },
    desktopMedia: {
        width: '100%',
        borderRadius: 8,
        display: 'block',
    },
    header: {
        color: TEAL,
        fontFamily: FONT,
        fontSize: 24,
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: 'normal',
        margin: `0 0 ${CONTENT_GAP}px 0 !important`,
        [theme.breakpoints.down('sm')]: {
            fontSize: 20,
        },
    },
    paragraphContainer: {
        padding: '16px 0',
        [theme.breakpoints.down('sm')]: {
            padding: 0,
        },
    },
    paragraph: {
        color: '#000',
        fontFamily: FONT,
        fontSize: 18,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
        margin: 0,
        marginBottom: CONTENT_GAP,
        [theme.breakpoints.down('sm')]: {
            fontSize: 14,
        },
    },
    linkRow: {
        color: '#000',
        fontFamily: FONT,
        fontSize: 18,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
        margin: 0,
        marginBottom: CONTENT_GAP,
        '&:last-child': {
            marginBottom: 0,
        },
        [theme.breakpoints.down('sm')]: {
            fontSize: 14,
        },
    },
    link: {
        color: TEAL,
        textDecoration: 'underline',
    },
    attachmentLink: {
        fontWeight: 700,
    },
    actions: {
        padding: `${theme.spacing(2)}px ${theme.spacing(4)}px`,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: {
            paddingTop: 0,
            paddingBottom: theme.spacing(2),
            paddingLeft: 20,
            paddingRight: 20,
            flexDirection: 'column',
            alignItems: 'stretch',
        },
    },
    checkboxLabel: {
        color: '#000',
        fontFamily: FONT,
        fontSize: 16,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
        [theme.breakpoints.down('sm')]: {
            fontSize: 14,
        },
    },
    checkbox: {
        // The app theme sets palette.primary.main = '#fff', which would make
        // the checked-state icon invisible. Force the brand purple on check.
        '&.Mui-checked': {
            color: PURPLE,
        },
    },
    closeButton: {
        backgroundColor: PURPLE,
        color: '#fff',
        minWidth: 120,
        borderRadius: 4,
        textTransform: 'none',
        fontFamily: FONT,
        fontSize: 16,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
        padding: '12px 20px',
        '&:hover': {
            backgroundColor: '#4a2c72',
        },
        [theme.breakpoints.down('sm')]: {
            minWidth: 96,
            // Right-align the Close button within its row on mobile.
            alignSelf: 'flex-end',
        },
    },
}));

/**
 * After-login notification popup.
 *
 * Props:
 *  - notification: notification content returned by the API
 *  - open: boolean
 *  - onClose: ({ hideFuture: boolean }) => void
 */
export default function AfterLoginNotification({ notification, open, onClose }) {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [dontShow, setDontShow] = useState(false);

    useEffect(() => {
        setDontShow(false);
    }, [notification?.distr_notification_id]);

    const handleClose = useCallback(() => {
        onClose({ hideFuture: dontShow });
    }, [dontShow, onClose]);

    const toAbsoluteUrl = (url) => {
        if (!url) return '';
        return /^https?:\/\//i.test(url) ? url : getWebAbsPath(url);
    };

    const mediaSrc = toAbsoluteUrl(notification?.media_url);
    const titleText = notification?.title || '';
    const attachmentUrl = toAbsoluteUrl(notification?.attachment_url);
    const learnMoreUrl = toAbsoluteUrl(notification?.learn_more_url);
    const showAttachment = attachmentUrl && notification?.attachment_link_text;
    const showLearnMore = learnMoreUrl && notification?.learn_more_link_text;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            classes={{ paper: classes.paper }}
            maxWidth={false}
            scroll="paper"
            fullScreen={false}
            aria-labelledby="after-login-notification-title"
        >
            <DialogContent className={classes.content}>
                <div className={classes.row}>
                    <div className={classes.leftCol}>
                        <h2 id="after-login-notification-title" className={classes.title} style={{whiteSpace:'pre-wrap'}}  >
                            {JSON.parse(`"${titleText}"`)}
                        </h2>
                        <div className={classes.innerContent}>
                            {isMobile && mediaSrc ? (
                                <img className={classes.mobileMedia} src={mediaSrc} alt={titleText} />
                            ) : null}
                            {notification?.header ? <h3 className={classes.header}>{notification.header}</h3> : null}
                            <div className={classes.paragraphContainer}>
                                {notification?.description_1 ? (
                                    <p className={classes.paragraph}>{notification.description_1}</p>
                                ) : null}
                                {notification?.description_2 ? (
                                    <p className={classes.paragraph}>{notification.description_2}</p>
                                ) : null}
                            </div>
                            {showAttachment ? (
                                <p className={classes.linkRow}>
                                    {notification.attachment_label}{' '}
                                    <a
                                        className={clsx(classes.link, classes.attachmentLink)}
                                        href={attachmentUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {notification.attachment_link_text}
                                    </a>
                                </p>
                            ) : null}
                            {showLearnMore ? (
                                <p className={classes.linkRow}>
                                    {notification.learn_more_label}{' '}
                                    <a
                                        className={classes.link}
                                        href={learnMoreUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {notification.learn_more_link_text}
                                    </a>
                                </p>
                            ) : null}
                        </div>
                    </div>
                    {!isMobile && mediaSrc ? (
                        <div className={classes.rightCol}>
                            <img className={classes.desktopMedia} src={mediaSrc} alt={titleText} />
                        </div>
                    ) : null}
                </div>
            </DialogContent>
            <DialogActions className={classes.actions}>
                <FormControlLabel
                    classes={{ label: classes.checkboxLabel }}
                    control={
                        <Checkbox
                            className={classes.checkbox}
                            checked={dontShow}
                            onChange={(e) => setDontShow(e.target.checked)}
                        />
                    }
                    label={notification?.do_not_show_text}
                />
                <Button className={classes.closeButton} onClick={handleClose} variant="contained">
                    {notification?.close_text}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
